import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { 
  MessageCircle, Search, Send, Phone, Video, MoreVertical, 
  Paperclip, Smile, Mic, User, Check, CheckCheck, Clock,
  Filter, Bot, UserCircle, Loader2, X, Plus, ChevronDown, ChevronRight,
  Mail, MapPin, Globe, Tag, Sparkles, CheckCircle, Columns, LayoutGrid, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function WhatsAppSupport() {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [focusedChatId, setFocusedChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [chatInputs, setChatInputs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [multiChatMode, setMultiChatMode] = useState(true);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [expandedSection, setExpandedSection] = useState('contact');

  const chatEndRefs = useRef({});

  const scrollToBottom = (convId) => {
    chatEndRefs.current[convId]?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch support customers from database
  const fetchConversations = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/customers?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const custs = data.customers || [];
        const mapped = custs.map(cust => ({
          id: cust.id,
          name: cust.company_name || 'Healthcare - Malaysia',
          picName: cust.name,
          phone: cust.phone,
          email: cust.email,
          role: cust.role || 'PIC',
          status: cust.status || 'Customer',
          issue: cust.issue || 'No reported issues',
          tags: cust.tags || [],
          lastMessage: cust.issue || 'No messages yet',
          time: '12:00 PM',
          unread: 0,
          avatar: cust.company_name ? cust.company_name.charAt(0).toUpperCase() : 'C',
          isAI: cust.status === 'Customer'
        }));
        setConversations(mapped);
      } else {
        toast.error('Failed to load customers');
      }
    } catch (error) {
      console.error('Fetch conversations error:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [token]);

  const fetchMessagesForChat = async (convId, conv) => {
    try {
      const response = await fetch(`${API}/api/whatsapp/messages/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const dbMsgs = data.messages || [];
        
        if (dbMsgs.length === 0) {
          // Welcome messages if no messages exist
          const initialMsgs = [
            {
              id: 'init-1',
              sender: 'customer',
              content: conv.issue || 'Hi support team, I need assistance.',
              time: '10:00 AM',
              status: 'read'
            },
            {
              id: 'init-2',
              sender: 'ai',
              content: `Hello! I'm the AI Support Assistant. I've analyzed your requirement: "${conv.issue}". How can I help you resolve this?`,
              time: '10:01 AM',
              status: 'read'
            }
          ];
          setChatMessages(prev => ({ ...prev, [convId]: initialMsgs }));
        } else {
          const mappedMsgs = dbMsgs.map((m, idx) => ({
            id: m.id || idx,
            sender: m.direction === 'incoming' ? 'customer' : (m.sender === 'AI' || m.sender === 'ai' ? 'ai' : 'agent'),
            content: m.content,
            time: new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: m.status || 'delivered'
          }));
          setChatMessages(prev => ({ ...prev, [convId]: mappedMsgs }));
        }
      }
    } catch (error) {
      console.error('Fetch messages error:', error);
    }
  };

  // Poll for new messages periodically for active chats
  useEffect(() => {
    if (!token || activeChats.length === 0) return;
    
    const interval = setInterval(() => {
      activeChats.forEach(chat => {
        fetchMessagesForChat(chat.id, chat);
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [activeChats, token]);

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          conv.picName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          conv.phone.includes(searchTerm);
    const matchesFilter = filterType === 'all' || 
                          (filterType === 'ai' && conv.isAI) ||
                          (filterType === 'agent' && !conv.isAI);
    return matchesSearch && matchesFilter;
  });

  const handleSelectConversation = async (conv) => {
    const exists = activeChats.some(c => c.id === conv.id);
    
    let updatedChats = activeChats;
    if (multiChatMode) {
      if (!exists) {
        if (activeChats.length >= 3) {
          toast.warning("You can open up to 3 chats simultaneously.");
          return;
        }
        updatedChats = [...activeChats, conv];
        setActiveChats(updatedChats);
      }
      setFocusedChatId(conv.id);
    } else {
      updatedChats = [conv];
      setActiveChats(updatedChats);
      setFocusedChatId(conv.id);
    }
    
    await fetchMessagesForChat(conv.id, conv);
  };

  const handleCloseChat = (convId, e) => {
    e.stopPropagation();
    const updatedChats = activeChats.filter(c => c.id !== convId);
    setActiveChats(updatedChats);
    if (focusedChatId === convId) {
      setFocusedChatId(updatedChats.length > 0 ? updatedChats[updatedChats.length - 1].id : null);
    }
  };

  const handleSendMessage = async (convId) => {
    const text = chatInputs[convId] || '';
    if (!text.trim()) return;
    
    const chat = activeChats.find(c => c.id === convId);
    if (!chat) return;

    const currentMessages = chatMessages[convId] || [];
    const tempId = 'temp-' + Date.now();
    const newMsg = {
      id: tempId,
      sender: 'agent',
      content: text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: 'sending'
    };
    
    setChatMessages(prev => ({
      ...prev,
      [convId]: [...currentMessages, newMsg]
    }));
    
    setChatInputs(prev => ({ ...prev, [convId]: '' }));
    
    try {
      // POST message to backend
      const response = await fetch(`${API}/api/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contact_id: convId,
          message: text,
          phone: chat.phone
        })
      });
      
      if (response.ok) {
        setChatMessages(prev => {
          const msgs = prev[convId] || [];
          return {
            ...prev,
            [convId]: msgs.map(m => m.id === tempId ? { ...m, status: 'delivered' } : m)
          };
        });
        
        // Auto-reply with AI if isAI is enabled
        if (chat.isAI) {
          setTimeout(async () => {
            const aiTempId = 'ai-typing-' + Date.now();
            setChatMessages(prev => ({
              ...prev,
              [convId]: [...(prev[convId] || []), {
                id: aiTempId,
                sender: 'ai',
                content: 'AI is thinking...',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                status: 'sending'
              }]
            }));
            
            try {
              const aiResponse = await fetch(`${API}/api/support/chat/ai-reply`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                  customer_id: convId,
                  message: text
                })
              });
              
              if (aiResponse.ok) {
                const aiData = await aiResponse.json();
                const replyText = aiData.reply;
                
                setChatMessages(prev => {
                  const msgs = (prev[convId] || []).filter(m => m.id !== aiTempId);
                  return {
                    ...prev,
                    [convId]: [...msgs, {
                      id: 'ai-' + Date.now(),
                      sender: 'ai',
                      content: replyText,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                      status: 'delivered'
                    }]
                  };
                });
              } else {
                setChatMessages(prev => ({
                  ...prev,
                  [convId]: (prev[convId] || []).filter(m => m.id !== aiTempId)
                }));
              }
            } catch (aiErr) {
              console.error(aiErr);
              setChatMessages(prev => ({
                ...prev,
                [convId]: (prev[convId] || []).filter(m => m.id !== aiTempId)
              }));
            }
          }, 1200);
        }
      } else {
        toast.error('Failed to send message');
        setChatMessages(prev => {
          const msgs = prev[convId] || [];
          return {
            ...prev,
            [convId]: msgs.map(m => m.id === tempId ? { ...m, status: 'failed' } : m)
          };
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to send message');
    }
  };

  const handleAddTag = async (convId) => {
    if (!tagInput.trim()) return;
    const chat = activeChats.find(c => c.id === convId);
    if (!chat) return;

    const currentTags = chat.tags || [];
    if (currentTags.includes(tagInput.trim())) {
      toast.error('Tag already exists');
      return;
    }

    const updatedTags = [...currentTags, tagInput.trim()];

    try {
      const response = await fetch(`${API}/api/support/customers/${convId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ tags: updatedTags })
      });

      if (response.ok) {
        setActiveChats(prev => prev.map(c => c.id === convId ? { ...c, tags: updatedTags } : c));
        setConversations(prev => prev.map(c => c.id === convId ? { ...c, tags: updatedTags } : c));
        setTagInput('');
        setShowTagInput(false);
        toast.success('Tag added');
      } else {
        toast.error('Failed to update tags');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update tags');
    }
  };

  const handleRemoveTag = async (convId, tagText) => {
    const chat = activeChats.find(c => c.id === convId);
    if (!chat) return;

    const currentTags = chat.tags || [];
    const updatedTags = currentTags.filter(t => t !== tagText);

    try {
      const response = await fetch(`${API}/api/support/customers/${convId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ tags: updatedTags })
      });

      if (response.ok) {
        setActiveChats(prev => prev.map(c => c.id === convId ? { ...c, tags: updatedTags } : c));
        setConversations(prev => prev.map(c => c.id === convId ? { ...c, tags: updatedTags } : c));
        toast.success('Tag removed');
      } else {
        toast.error('Failed to remove tag');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove tag');
    }
  };

  const handleInputChange = (convId, val) => {
    setChatInputs(prev => ({ ...prev, [convId]: val }));
  };

  const toggleAICopilot = (convId) => {
    setActiveChats(prev => prev.map(c => {
      if (c.id === convId) {
        const nextState = !c.isAI;
        toast.success(`AI Copilot turned ${nextState ? 'ON' : 'OFF'} for ${c.name}`);
        return { ...c, isAI: nextState };
      }
      return c;
    }));
  };

  const handleCall = (chat) => {
    toast.info(`Calling PIC ${chat.picName || chat.name} at ${chat.phone}...`);
  };

  const handleAttachFile = (chatId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        toast.success(`Attached file: ${file.name}`);
      }
    };
    input.click();
  };

  useEffect(() => {
    activeChats.forEach(chat => {
      scrollToBottom(chat.id);
    });
  }, [chatMessages, activeChats]);

  // Set default chat on mount if list is available
  useEffect(() => {
    if (conversations.length > 0 && activeChats.length === 0) {
      handleSelectConversation(conversations[0]);
    }
  }, [conversations]);

  const getStatusColor = (status) => {
    const colors = {
      'Customer': 'bg-blue-500',
      'Lead': 'bg-amber-500',
      'Inactive': 'bg-zinc-500'
    };
    return colors[status] || 'bg-zinc-500';
  };

  const getMessageStatus = (status) => {
    if (status === 'read') return <CheckCheck className="w-4 h-4 text-blue-400" />;
    if (status === 'delivered') return <CheckCheck className="w-4 h-4 text-muted-foreground" />;
    if (status === 'sending') return <Clock className="w-3 h-3 text-muted-foreground" />;
    return <Check className="w-4 h-4 text-muted-foreground" />;
  };

  const focusedChat = activeChats.find(c => c.id === focusedChatId);
  const focusedProfile = focusedChat ? {
    id: focusedChat.id,
    email: focusedChat.email,
    phone: focusedChat.phone,
    role: focusedChat.role,
    status: focusedChat.status,
    issue: focusedChat.issue,
    channels: ['whatsapp']
  } : null;
  const focusedTags = focusedChat ? (focusedChat.tags || []) : [];

  return (
    <div className="h-[calc(100vh-140px)] flex gap-6 animate-fade-in">
      {/* Column 1: Conversations List */}
      <div className="w-80 flex flex-col support-card p-0 overflow-hidden shrink-0 border border-border">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              WhatsApp Support
            </h2>
            {/* Multi-Chat Mode Toggle */}
            <button
              onClick={() => {
                setMultiChatMode(!multiChatMode);
                toast.success(`Multi-Chat mode turned ${!multiChatMode ? 'ON' : 'OFF'}`);
                if (multiChatMode && activeChats.length > 1) {
                  setActiveChats(activeChats.filter(c => c.id === focusedChatId));
                }
              }}
              className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium border ${
                multiChatMode 
                  ? 'bg-primary/20 text-primary border-primary/30' 
                  : 'bg-secondary text-muted-foreground border-transparent hover:bg-secondary/80'
              }`}
              title="Toggle Multi-Chat Layout"
            >
              <Columns className="w-3.5 h-3.5" />
              <span>Multi-Chat</span>
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="support-input pl-10 text-sm"
              data-testid="conversation-search"
            />
          </div>
          <div className="flex gap-2 mt-3">
            {['all', 'ai', 'agent'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors capitalize ${
                  filterType === type 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {type === 'all' ? 'All' : type === 'ai' ? 'AI Handled' : 'Agent'}
              </button>
            ))}
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground">Loading customers...</p>
            </div>
          ) : filteredConversations.length > 0 ? (
            filteredConversations.map((conv) => {
              const isOpen = activeChats.some(c => c.id === conv.id);
              const isFocused = focusedChatId === conv.id;
              return (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-4 border-b border-border/50 cursor-pointer transition-colors relative ${
                    isFocused 
                      ? 'bg-primary/10 border-l-4 border-l-primary' 
                      : isOpen 
                        ? 'bg-secondary/30' 
                        : 'hover:bg-secondary/50'
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                        {conv.avatar}
                      </div>
                      {conv.isAI && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center border border-card" title="AI Controlled">
                          <Bot className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-semibold text-sm truncate">{conv.name}</span>
                        <span className="text-3xs text-muted-foreground">{conv.time}</span>
                      </div>
                      <p className="text-2xs text-primary/80 font-medium truncate mb-1">PIC: {conv.picName}</p>
                      <p className="text-xs text-muted-foreground truncate">{conv.lastMessage}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(conv.status)}`}></span>
                        <span className="text-3xs text-muted-foreground capitalize">{conv.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-muted-foreground text-sm">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              No conversations found
            </div>
          )}
        </div>
      </div>

      {/* Column 2: Chat Area */}
      <div className="flex-1 flex gap-4 min-w-0 h-full">
        {activeChats.length > 0 ? (
          <div className={`flex-1 grid gap-4 h-full ${
            activeChats.length === 1 
              ? 'grid-cols-1' 
              : activeChats.length === 2 
                ? 'grid-cols-2' 
                : 'grid-cols-3'
          }`}>
            {activeChats.map((chat) => {
              const messages = chatMessages[chat.id] || [];
              const isFocused = focusedChatId === chat.id;
              
              return (
                <div
                  key={chat.id}
                  onClick={() => setFocusedChatId(chat.id)}
                  className={`flex flex-col support-card p-0 overflow-hidden border-2 h-full transition-all relative ${
                    isFocused 
                      ? 'border-primary ring-2 ring-primary/10 shadow-lg' 
                      : 'border-border/60 hover:border-border'
                  }`}
                >
                  {/* Chat Window Header */}
                  <div className="p-3 border-b border-border flex items-center justify-between bg-card">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium text-sm">
                          {chat.avatar}
                        </div>
                        {chat.isAI && (
                          <div className="absolute -bottom-1 -right-1 w-4.5 h-4.5 bg-violet-500 rounded-full flex items-center justify-center border border-card">
                            <Bot className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="truncate">
                        <h3 className="font-semibold text-sm truncate leading-snug">{chat.name}</h3>
                        <p className="text-xs text-muted-foreground leading-none truncate">PIC: {chat.picName} ({chat.phone})</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => handleCall(chat)}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground" 
                        title="Call"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => handleCloseChat(chat.id, e)}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/10">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'customer' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div className={`max-w-[85%] ${
                          msg.sender === 'customer' 
                            ? 'chat-bubble-user' 
                            : msg.sender === 'ai' 
                              ? 'chat-bubble-ai' 
                              : 'chat-bubble-agent'
                        }`}>
                          {msg.sender !== 'customer' && (
                            <div className="flex items-center gap-1.5 mb-0.5 text-2xs text-muted-foreground/80 font-semibold uppercase">
                              {msg.sender === 'ai' ? (
                                <>
                                  <Bot className="w-2.5 h-2.5 text-violet-400" />
                                  <span>AI Copilot</span>
                                </>
                              ) : (
                                <>
                                  <UserCircle className="w-2.5 h-2.5 text-emerald-400" />
                                  <span>You</span>
                                </>
                              )}
                            </div>
                          )}
                          <p className="text-xs whitespace-pre-line leading-relaxed">{msg.content}</p>
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-3xs text-muted-foreground/60">{msg.time}</span>
                            {msg.sender !== 'customer' && getMessageStatus(msg.status)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={el => chatEndRefs.current[chat.id] = el} />
                  </div>

                  {/* AI Copilot Status Banner */}
                  <div className="mx-2 mb-2 p-2 rounded-lg flex items-center gap-2 border transition-all duration-300 bg-violet-500/10 border-violet-500/20">
                    <Bot className={`w-4 h-4 shrink-0 ${chat.isAI ? 'text-violet-400 animate-pulse' : 'text-muted-foreground'}`} />
                    <p className="text-2xs font-medium text-violet-300 truncate">
                      {chat.isAI ? 'AI auto-responding enabled.' : 'Agent handling (AI disabled).'}
                    </p>
                    <button 
                      onClick={() => toggleAICopilot(chat.id)} 
                      className="ml-auto bg-violet-500 hover:bg-violet-600 text-white text-3xs px-2.5 py-0.5 rounded transition-colors font-semibold"
                    >
                      {chat.isAI ? 'Take Over' : 'Enable AI'}
                    </button>
                  </div>

                  {/* Typing Input */}
                  <div className="p-3 border-t border-border bg-card">
                    <div className="flex items-center gap-2">
                       <button 
                        onClick={() => handleAttachFile(chat.id)}
                        className="p-1 hover:bg-secondary rounded" 
                        title="Attach file"
                      >
                        <Paperclip className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <input
                        type="text"
                        value={chatInputs[chat.id] || ''}
                        onChange={(e) => handleInputChange(chat.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSendMessage(chat.id);
                          }
                        }}
                        placeholder="Type a message..."
                        className="support-input text-xs flex-1 h-8 min-w-0"
                      />
                      <button
                        onClick={() => handleSendMessage(chat.id)}
                        disabled={!(chatInputs[chat.id] || '').trim()}
                        className="support-btn-primary p-2 flex items-center justify-center shrink-0"
                      >
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 support-card flex items-center justify-center border border-border">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Select a Conversation</h3>
              <p className="text-muted-foreground text-sm">Choose a support customer from the left sidebar to open the chat interface</p>
            </div>
          </div>
        )}
      </div>

      {/* Column 3: Contact Details Sidebar (Right Panel) */}
      <div 
        className={`support-card flex flex-col p-0 overflow-y-auto shrink-0 bg-card transition-all duration-300 overflow-hidden ${
          focusedChat && focusedProfile && activeChats.length < 2
            ? 'w-80 border border-border opacity-100 animate-slide-in-right'
            : 'w-0 border-0 opacity-0'
        }`}
      >
        {focusedChat && focusedProfile && (
          <>
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm">Customer Profile</h3>
            <span className="text-2xs bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded font-bold uppercase">{focusedChat.role}</span>
          </div>

          {/* Profile Summary */}
          <div className="p-5 flex flex-col items-center text-center border-b border-border bg-secondary/10">
            <div className="relative mb-3">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-xl border border-primary/30">
                {focusedChat.avatar}
              </div>
              <div className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-card" title="Online" />
            </div>
            <h4 className="font-bold text-base leading-snug">{focusedChat.name}</h4>
            <p className="text-xs text-muted-foreground mt-1">PIC: <span className="font-semibold text-foreground">{focusedChat.picName}</span></p>
            <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-3xs px-2.5 py-0.5 rounded-full font-semibold mt-2.5 uppercase">
              {focusedChat.status}
            </span>
          </div>

          {/* Collapsible Accordion Sections */}
          <div className="flex-1 divide-y divide-border/60">
            {/* Contact Details */}
            <div>
              <button 
                onClick={() => setExpandedSection(expandedSection === 'contact' ? '' : 'contact')}
                className="w-full flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors text-left"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Information</span>
                {expandedSection === 'contact' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expandedSection === 'contact' && (
                <div className="px-4 pb-4 space-y-3.5 text-xs">
                  <div className="flex items-start gap-2.5">
                    <Mail className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-2xs text-muted-foreground">Email</p>
                      <p className="font-medium break-all">{focusedProfile.email || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Phone className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-2xs text-muted-foreground">Phone</p>
                      <p className="font-medium font-mono">{focusedProfile.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-2xs text-muted-foreground">Location</p>
                      <p className="font-medium">Healthcare - Malaysia</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-2xs text-muted-foreground">Language</p>
                      <p className="font-medium">English</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Issue Description accordion */}
            <div>
              <button 
                onClick={() => setExpandedSection(expandedSection === 'issue' ? '' : 'issue')}
                className="w-full flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors text-left"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5 text-amber-500" />
                  Reported Issue
                </span>
                {expandedSection === 'issue' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {expandedSection === 'issue' && (
                <div className="px-4 pb-4 text-xs">
                  <div className="p-3 bg-secondary/30 border border-border/40 rounded-lg whitespace-pre-line text-foreground/90 leading-relaxed font-normal">
                    {focusedProfile.issue || 'No reported issues.'}
                  </div>
                </div>
              )}
            </div>

            {/* Tags Manager */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-primary" />
                  Tags
                </span>
                <button
                  onClick={() => setShowTagInput(!showTagInput)}
                  className="p-1 hover:bg-secondary rounded text-primary hover:text-primary-foreground flex items-center gap-0.5 text-2xs font-medium"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </button>
              </div>

              {/* Tag Insertion Field */}
              {showTagInput && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Enter tag..."
                    className="support-input text-xs flex-1 h-7"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTag(focusedChat.id);
                    }}
                  />
                  <button
                    onClick={() => handleAddTag(focusedChat.id)}
                    className="support-btn-primary px-2 text-2xs"
                  >
                    Save
                  </button>
                </div>
              )}

              {/* Tag Bubbles */}
              <div className="flex flex-wrap gap-1.5">
                {focusedTags.map(tag => (
                  <span 
                    key={tag}
                    className="flex items-center gap-1 bg-secondary text-foreground text-3xs px-2 py-1 rounded-md border border-border/60 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/20 transition-all cursor-pointer group"
                    onClick={() => handleRemoveTag(focusedChat.id, tag)}
                    title="Click to remove tag"
                  >
                    {tag}
                    <X className="w-2.5 h-2.5 text-muted-foreground group-hover:text-red-400" />
                  </span>
                ))}
                {focusedTags.length === 0 && (
                  <span className="text-3xs text-muted-foreground italic">No tags assigned.</span>
                )}
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
